/**
 * Coupon campaign manager — the console's working surface.
 *
 * Create, edit, duplicate, activate, pause, archive, restore and delete
 * campaigns; search, filter and sort them; import and export in bulk; and open
 * any single campaign to see how it is performing.
 *
 * This screen only ever writes to the `coupons` table and the audit log. It
 * changes nothing about checkout, payment gateways, webhooks or memberships:
 * the engine reads the same columns it always has.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import * as XLSX from "xlsx";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import { Separator } from "@/components/ui/separator";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Archive,
  Copy,
  Download,
  Eye,
  FileUp,
  MoreHorizontal,
  Pause,
  Pencil,
  Play,
  Plus,
  RefreshCw,
  RotateCcw,
  Search,
  Ticket,
  Trash2,
} from "lucide-react";
import CouponForm from "./CouponForm";
import CouponAuditLog from "./CouponAuditLog";
import {
  BENEFIT_TYPE_OPTIONS,
  CSV_TEMPLATE,
  STATUS_OPTIONS,
  auditIpHash,
  benefitTypeLabel,
  couponsToRecords,
  downloadBlob,
  emptyCouponForm,
  formFromRow,
  formatMoney,
  parseCouponCsv,
  payloadFromForm,
  toCsv,
  type BenefitStatusDb,
  type CouponFormValues,
  type CouponRow,
  type ImportRow,
} from "@/lib/couponConsole";

type SortKey = "created_at" | "code" | "redemption_count" | "valid_until" | "status";

const STATUS_TONE: Record<string, string> = {
  active: "bg-primary/15 text-primary border-primary/30",
  draft: "bg-muted text-muted-foreground",
  scheduled: "bg-accent/20 text-accent-foreground",
  paused: "bg-muted text-muted-foreground",
  expired: "bg-muted text-muted-foreground",
  archived: "bg-muted text-muted-foreground",
};

interface Stats {
  ok?: boolean;
  attempts?: number;
  uses?: number;
  revenue?: number;
  discount_given?: number;
  avg_order?: number;
  conversion_pct?: number;
  remaining_uses?: number | null;
  redemption_count?: number;
  top_institutions?: Array<{ name: string; uses: number }>;
  top_partners?: Array<{ name: string; uses: number }>;
  countries_used?: Array<{ currency: string; uses: number }>;
}

const CouponsManager = ({
  isSuperAdmin,
  onCouponsLoaded,
}: {
  isSuperAdmin: boolean;
  onCouponsLoaded?: (rows: CouponRow[]) => void;
}) => {
  const [rows, setRows] = useState<CouponRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [showDeleted, setShowDeleted] = useState(false);
  const [sortKey, setSortKey] = useState<SortKey>("created_at");
  const [sortAsc, setSortAsc] = useState(false);
  const [selected, setSelected] = useState<string[]>([]);

  const [institutions, setInstitutions] = useState<Array<{ id: string; name: string }>>([]);
  const [formState, setFormState] = useState<
    { mode: "create" | "edit" | "duplicate"; values: CouponFormValues; id?: string } | null
  >(null);
  const [detail, setDetail] = useState<CouponRow | null>(null);
  const [detailStats, setDetailStats] = useState<Stats | null>(null);
  const [confirm, setConfirm] = useState<
    { row: CouponRow; kind: "soft" | "hard" } | null
  >(null);
  const [importOpen, setImportOpen] = useState(false);
  const [importRows, setImportRows] = useState<ImportRow[]>([]);
  const [importFatal, setImportFatal] = useState<string | null>(null);

  /* -------------------------------------------------------------- loading */

  const load = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("coupons")
      .select("*")
      .order("created_at", { ascending: false })
      .limit(1000);
    if (error) {
      toast.error("Could not load campaigns", { description: error.message });
    } else {
      const list = (data ?? []) as unknown as CouponRow[];
      setRows(list);
      onCouponsLoaded?.(list);
    }
    setLoading(false);
  }, [onCouponsLoaded]);

  useEffect(() => {
    void load();
    void (async () => {
      const { data } = await supabase
        .from("organizations")
        .select("id, name")
        .order("name")
        .limit(200);
      setInstitutions((data ?? []) as Array<{ id: string; name: string }>);
    })();
  }, [load]);

  /* --------------------------------------------------------------- audit */

  const logEvent = async (
    row: Pick<CouponRow, "id" | "code">,
    event: string,
    reason: string,
    oldValues?: unknown,
    newValues?: unknown,
  ) => {
    await supabase.rpc("log_coupon_admin_event", {
      p_coupon_id: row.id,
      p_coupon_code: row.code,
      p_event_type: event,
      p_reason: reason || null,
      p_ip_hash: await auditIpHash(),
      p_old: (oldValues ?? null) as never,
      p_new: (newValues ?? null) as never,
    });
  };

  /* ---------------------------------------------------------------- write */

  const handleSubmit = async (values: CouponFormValues, codes: string[], reason: string) => {
    if (!formState) return;
    setSaving(true);
    try {
      if (formState.mode === "edit" && formState.id) {
        const payload = payloadFromForm(values, values.code);
        const { error } = await supabase
          .from("coupons")
          .update(payload as never)
          .eq("id", formState.id);
        if (error) throw error;
        await logEvent({ id: formState.id, code: payload.code }, "update", reason);
        toast.success("Campaign updated");
      } else {
        const payloads = codes.map((code) => ({
          ...payloadFromForm(values, code),
          batch_id: codes.length > 1 ? crypto.randomUUID() : null,
        }));
        const { data, error } = await supabase
          .from("coupons")
          .insert(payloads as never)
          .select("id, code");
        if (error) throw error;
        if (formState.mode === "duplicate" && data?.[0]) {
          await logEvent(data[0] as CouponRow, "duplicate", reason);
        } else if (reason && data?.[0]) {
          await logEvent(data[0] as CouponRow, "create", reason);
        }
        toast.success(
          codes.length > 1 ? `${codes.length} codes created` : `${payloads[0].code} created`,
        );
        if (codes.length > 1) {
          downloadBlob(
            `codes-${payloads[0].code}.csv`,
            new Blob([toCsv(payloads.map((p) => ({ code: p.code, label: p.label })))], {
              type: "text/csv;charset=utf-8",
            }),
          );
        }
      }
      setFormState(null);
      await load();
    } catch (e) {
      toast.error("That did not save", {
        description: e instanceof Error ? e.message : "Please try again.",
      });
    } finally {
      setSaving(false);
    }
  };

  const patch = async (
    row: CouponRow,
    changes: Record<string, unknown>,
    event: string,
    successMessage: string,
  ) => {
    const { error } = await supabase.from("coupons").update(changes as never).eq("id", row.id);
    if (error) {
      toast.error("That did not save", { description: error.message });
      return;
    }
    await logEvent(row, event, "", null, changes);
    toast.success(successMessage);
    await load();
  };

  const setStatus = (row: CouponRow, status: BenefitStatusDb) => {
    const changes: Record<string, unknown> = { status, active: status === "active" };
    if (status === "archived") changes.archived_at = new Date().toISOString();
    const event =
      status === "active" ? "activate" : status === "paused" ? "pause" : status === "archived" ? "archive" : "update";
    void patch(row, changes, event, `${row.code} is now ${status}`);
  };

  const softDelete = (row: CouponRow) =>
    void patch(
      row,
      { deleted_at: new Date().toISOString(), active: false, status: "archived" },
      "soft_delete",
      `${row.code} moved to deleted`,
    );

  const restore = (row: CouponRow) =>
    void patch(row, { deleted_at: null, status: "paused", active: false }, "restore", `${row.code} restored`);

  const hardDelete = async (row: CouponRow) => {
    const { error } = await supabase.rpc("hard_delete_coupon", {
      p_coupon_id: row.id,
      p_reason: "Permanent deletion from the admin console",
      p_ip_hash: await auditIpHash(),
    });
    if (error) {
      toast.error("Could not delete permanently", { description: error.message });
      return;
    }
    toast.success(`${row.code} deleted permanently`);
    await load();
  };

  const bulkStatus = async (status: BenefitStatusDb) => {
    const targets = rows.filter((r) => selected.includes(r.id));
    for (const row of targets) {
      // eslint-disable-next-line no-await-in-loop
      await patch(row, { status, active: status === "active" }, status === "active" ? "activate" : "pause", "");
    }
    toast.success(`${targets.length} campaigns updated`);
    setSelected([]);
  };

  /* -------------------------------------------------------------- detail */

  const openDetail = async (row: CouponRow) => {
    setDetail(row);
    setDetailStats(null);
    const { data, error } = await supabase.rpc("get_coupon_stats", { p_coupon_id: row.id });
    if (!error) setDetailStats((data ?? {}) as Stats);
  };

  /* ------------------------------------------------------- import/export */

  const handleFile = async (file: File) => {
    const text = await file.text();
    const result = parseCouponCsv(text, {
      existingCodes: rows.map((r) => r.code),
      isSuperAdmin,
    });
    setImportFatal(result.fatal ?? null);
    setImportRows(result.rows);
  };

  const commitImport = async () => {
    const good = importRows.filter((r) => Object.keys(r.errors).length === 0);
    if (good.length === 0) {
      toast.error("Nothing valid to import.");
      return;
    }
    setSaving(true);
    const { error } = await supabase
      .from("coupons")
      .insert(good.map((r) => payloadFromForm(r.values)) as never);
    setSaving(false);
    if (error) {
      toast.error("Import failed", { description: error.message });
      return;
    }
    toast.success(`${good.length} campaigns imported as drafts`);
    setImportOpen(false);
    setImportRows([]);
    await load();
  };

  const exportRows = (format: "csv" | "xlsx") => {
    const source = selected.length > 0 ? rows.filter((r) => selected.includes(r.id)) : filtered;
    if (source.length === 0) {
      toast.info("Nothing to export.");
      return;
    }
    const records = couponsToRecords(source);
    if (format === "csv") {
      downloadBlob("coupons.csv", new Blob([toCsv(records)], { type: "text/csv;charset=utf-8" }));
      return;
    }
    const sheet = XLSX.utils.json_to_sheet(records);
    const book = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(book, sheet, "Coupons");
    const buffer = XLSX.write(book, { bookType: "xlsx", type: "array" }) as ArrayBuffer;
    downloadBlob(
      "coupons.xlsx",
      new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      }),
    );
  };

  /* ------------------------------------------------------------ filtering */

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = rows.filter((r) => {
      if (showDeleted ? !r.deleted_at : !!r.deleted_at) return false;
      if (statusFilter !== "all" && r.status !== statusFilter) return false;
      if (typeFilter !== "all" && r.benefit_type !== typeFilter) return false;
      if (!q) return true;
      return [r.code, r.label, r.description, r.partner_org, ...(r.tags ?? [])]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q));
    });
    const dir = sortAsc ? 1 : -1;
    return [...list].sort((a, b) => {
      const av = (a as unknown as Record<string, unknown>)[sortKey];
      const bv = (b as unknown as Record<string, unknown>)[sortKey];
      if (av === bv) return a.code.localeCompare(b.code);
      if (av === null || av === undefined) return 1;
      if (bv === null || bv === undefined) return -1;
      return av > bv ? dir : -dir;
    });
  }, [rows, query, statusFilter, typeFilter, showDeleted, sortKey, sortAsc]);

  const toggleSort = (key: SortKey) => {
    if (key === sortKey) setSortAsc((v) => !v);
    else {
      setSortKey(key);
      setSortAsc(false);
    }
  };

  const allSelected = filtered.length > 0 && filtered.every((r) => selected.includes(r.id));

  /* ----------------------------------------------------------------- view */

  return (
    <div className="space-y-5">
      {/* Toolbar */}
      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-56">
          <Search
            className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground"
            aria-hidden="true"
          />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search code, name, tag or partner"
            className="pl-9 min-h-11"
            aria-label="Search campaigns"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-40 min-h-11" aria-label="Filter by status">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {STATUS_OPTIONS.map((s) => (
              <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-44 min-h-11" aria-label="Filter by benefit type">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All types</SelectItem>
            {BENEFIT_TYPE_OPTIONS.map((o) => (
              <SelectItem key={o.label} value={o.value}>{o.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button variant="outline" className="min-h-11" onClick={() => void load()}>
          <RefreshCw className="w-4 h-4 mr-2" aria-hidden="true" />
          Refresh
        </Button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" className="min-h-11">
              <Download className="w-4 h-4 mr-2" aria-hidden="true" />
              Export
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => exportRows("csv")}>CSV</DropdownMenuItem>
            <DropdownMenuItem onClick={() => exportRows("xlsx")}>Excel</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        <Button variant="outline" className="min-h-11" onClick={() => setImportOpen(true)}>
          <FileUp className="w-4 h-4 mr-2" aria-hidden="true" />
          Import
        </Button>
        <Button
          className="min-h-11"
          onClick={() => setFormState({ mode: "create", values: emptyCouponForm() })}
        >
          <Plus className="w-4 h-4 mr-2" aria-hidden="true" />
          New campaign
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-4">
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <Checkbox checked={showDeleted} onCheckedChange={(v) => setShowDeleted(!!v)} />
          Show deleted
        </label>
        {selected.length > 0 && (
          <>
            <Separator orientation="vertical" className="h-6" />
            <span className="text-sm text-muted-foreground">{selected.length} selected</span>
            <Button size="sm" variant="secondary" className="min-h-9" onClick={() => void bulkStatus("active")}>
              Activate
            </Button>
            <Button size="sm" variant="secondary" className="min-h-9" onClick={() => void bulkStatus("paused")}>
              Pause
            </Button>
            <Button size="sm" variant="ghost" className="min-h-9" onClick={() => setSelected([])}>
              Clear
            </Button>
          </>
        )}
      </div>

      {/* Table */}
      {loading ? (
        <div className="space-y-3">
          {[0, 1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-16 w-full rounded-3xl" />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <Card className="rounded-3xl">
          <CardContent className="p-10 text-center space-y-4">
            <Ticket className="w-10 h-10 mx-auto text-muted-foreground" aria-hidden="true" />
            <div>
              <p className="font-medium">No campaigns here yet</p>
              <p className="text-sm text-muted-foreground mt-1">
                Create your first campaign, or import a batch from a spreadsheet.
              </p>
            </div>
            <Button
              className="min-h-12"
              onClick={() => setFormState({ mode: "create", values: emptyCouponForm() })}
            >
              <Plus className="w-4 h-4 mr-2" aria-hidden="true" />
              New campaign
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Card className="rounded-3xl overflow-hidden">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10">
                    <Checkbox
                      checked={allSelected}
                      aria-label="Select all campaigns"
                      onCheckedChange={(v) =>
                        setSelected(v ? filtered.map((r) => r.id) : [])
                      }
                    />
                  </TableHead>
                  {(
                    [
                      ["code", "Code"],
                      ["status", "Status"],
                      ["redemption_count", "Used"],
                      ["valid_until", "Ends"],
                      ["created_at", "Created"],
                    ] as Array<[SortKey, string]>
                  ).map(([key, label]) => (
                    <TableHead key={key}>
                      <button
                        type="button"
                        className="hover:text-foreground min-h-9"
                        onClick={() => toggleSort(key)}
                        aria-label={`Sort by ${label}`}
                      >
                        {label}
                        {sortKey === key ? (sortAsc ? " ↑" : " ↓") : ""}
                      </button>
                    </TableHead>
                  ))}
                  <TableHead>Discount</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell>
                      <Checkbox
                        checked={selected.includes(r.id)}
                        aria-label={`Select ${r.code}`}
                        onCheckedChange={(v) =>
                          setSelected((prev) =>
                            v ? [...prev, r.id] : prev.filter((id) => id !== r.id),
                          )
                        }
                      />
                    </TableCell>
                    <TableCell>
                      <button
                        type="button"
                        className="text-left min-h-9"
                        onClick={() => void openDetail(r)}
                      >
                        <span className="font-mono font-medium">{r.code}</span>
                        <span className="block text-xs text-muted-foreground">
                          {r.label} · {benefitTypeLabel(r.benefit_type, r.tags ?? [])}
                        </span>
                      </button>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className={STATUS_TONE[r.status] ?? ""}>
                        {r.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm">
                      {r.redemption_count}
                      {r.max_redemptions ? ` / ${r.max_redemptions}` : ""}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {r.valid_until ? new Date(r.valid_until).toLocaleDateString() : "—"}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {new Date(r.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell className="text-sm">
                      {r.discount_type === "percent"
                        ? `${r.discount_value}%`
                        : r.discount_type === "full"
                          ? "Free"
                          : formatMoney(r.discount_value, r.currency)}
                    </TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-11 w-11" aria-label={`Actions for ${r.code}`}>
                            <MoreHorizontal className="w-4 h-4" aria-hidden="true" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => void openDetail(r)}>
                            <Eye className="w-4 h-4 mr-2" aria-hidden="true" />
                            View details
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() =>
                              setFormState({ mode: "edit", values: formFromRow(r), id: r.id })
                            }
                          >
                            <Pencil className="w-4 h-4 mr-2" aria-hidden="true" />
                            Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() =>
                              setFormState({
                                mode: "duplicate",
                                values: {
                                  ...formFromRow(r),
                                  code: "",
                                  status: "draft",
                                  label: `${r.label} (copy)`,
                                },
                              })
                            }
                          >
                            <Copy className="w-4 h-4 mr-2" aria-hidden="true" />
                            Duplicate
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          {r.status === "active" ? (
                            <DropdownMenuItem onClick={() => setStatus(r, "paused")}>
                              <Pause className="w-4 h-4 mr-2" aria-hidden="true" />
                              Pause
                            </DropdownMenuItem>
                          ) : (
                            <DropdownMenuItem onClick={() => setStatus(r, "active")}>
                              <Play className="w-4 h-4 mr-2" aria-hidden="true" />
                              Activate
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem onClick={() => setStatus(r, "archived")}>
                            <Archive className="w-4 h-4 mr-2" aria-hidden="true" />
                            Archive
                          </DropdownMenuItem>
                          {r.deleted_at ? (
                            <DropdownMenuItem onClick={() => restore(r)}>
                              <RotateCcw className="w-4 h-4 mr-2" aria-hidden="true" />
                              Restore
                            </DropdownMenuItem>
                          ) : (
                            <DropdownMenuItem
                              className="text-destructive"
                              onClick={() => setConfirm({ row: r, kind: "soft" })}
                            >
                              <Trash2 className="w-4 h-4 mr-2" aria-hidden="true" />
                              Delete
                            </DropdownMenuItem>
                          )}
                          {isSuperAdmin && (
                            <DropdownMenuItem
                              className="text-destructive"
                              onClick={() => setConfirm({ row: r, kind: "hard" })}
                            >
                              <Trash2 className="w-4 h-4 mr-2" aria-hidden="true" />
                              Delete permanently
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>
      )}

      {/* Create / edit */}
      <Dialog open={!!formState} onOpenChange={(open) => !open && setFormState(null)}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto rounded-3xl">
          <DialogHeader>
            <DialogTitle>
              {formState?.mode === "edit"
                ? "Edit campaign"
                : formState?.mode === "duplicate"
                  ? "Duplicate campaign"
                  : "New campaign"}
            </DialogTitle>
          </DialogHeader>
          {formState && (
            <CouponForm
              value={formState.values}
              mode={formState.mode}
              existingCodes={rows
                .filter((r) => r.id !== formState.id)
                .map((r) => r.code)}
              institutions={institutions}
              partners={institutions}
              isSuperAdmin={isSuperAdmin}
              saving={saving}
              onCancel={() => setFormState(null)}
              onSubmit={handleSubmit}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Detail */}
      <Sheet open={!!detail} onOpenChange={(open) => !open && setDetail(null)}>
        <SheetContent className="w-full sm:max-w-xl overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="font-mono">{detail?.code}</SheetTitle>
          </SheetHeader>
          {detail && (
            <div className="space-y-6 pt-4">
              <div>
                <p className="font-medium">{detail.label}</p>
                {detail.description && (
                  <p className="text-sm text-muted-foreground mt-1">{detail.description}</p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                {[
                  ["Status", detail.status],
                  ["Type", benefitTypeLabel(detail.benefit_type, detail.tags ?? [])],
                  [
                    "Discount",
                    detail.discount_type === "percent"
                      ? `${detail.discount_value}%`
                      : detail.discount_type === "full"
                        ? "Free"
                        : formatMoney(detail.discount_value, detail.currency),
                  ],
                  ["Regions", (detail.regions ?? []).join(", ") || "—"],
                  ["Used", `${detail.redemption_count}${detail.max_redemptions ? ` / ${detail.max_redemptions}` : ""}`],
                  [
                    "Remaining",
                    detailStats?.remaining_uses === null || detailStats?.remaining_uses === undefined
                      ? "Unlimited"
                      : String(detailStats.remaining_uses),
                  ],
                  ["Revenue", formatMoney(detailStats?.revenue ?? 0, detail.currency)],
                  ["Discount given", formatMoney(detailStats?.discount_given ?? 0, detail.currency)],
                  ["Average order", formatMoney(detailStats?.avg_order ?? 0, detail.currency)],
                  ["Conversion", `${detailStats?.conversion_pct ?? 0}%`],
                ].map(([label, value]) => (
                  <div key={label as string}>
                    <p className="text-xs text-muted-foreground">{label}</p>
                    <p className="text-sm font-medium mt-0.5">{value}</p>
                  </div>
                ))}
              </div>

              {(detailStats?.top_institutions?.length ?? 0) > 0 && (
                <div>
                  <p className="text-sm font-medium mb-2">Institutions using it</p>
                  {detailStats?.top_institutions?.map((i) => (
                    <p key={i.name} className="text-sm text-muted-foreground">
                      {i.name} · {i.uses}
                    </p>
                  ))}
                </div>
              )}

              <Separator />
              <div>
                <p className="text-sm font-medium mb-3">History</p>
                <CouponAuditLog couponId={detail.id} />
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>

      {/* Delete confirmation */}
      <AlertDialog open={!!confirm} onOpenChange={(open) => !open && setConfirm(null)}>
        <AlertDialogContent className="rounded-3xl">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirm?.kind === "hard" ? "Delete permanently?" : "Delete this campaign?"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirm?.kind === "hard"
                ? "This removes the campaign from the database for good. Only campaigns that have never been used can be deleted permanently — everything else must be archived."
                : "The campaign stops working immediately and moves to your deleted list. You can restore it at any time."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="min-h-12">Keep it</AlertDialogCancel>
            <AlertDialogAction
              className="min-h-12"
              onClick={() => {
                if (!confirm) return;
                if (confirm.kind === "hard") void hardDelete(confirm.row);
                else softDelete(confirm.row);
                setConfirm(null);
              }}
            >
              {confirm?.kind === "hard" ? "Delete permanently" : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Import */}
      <Dialog open={importOpen} onOpenChange={setImportOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto rounded-3xl">
          <DialogHeader>
            <DialogTitle>Import campaigns</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Upload a CSV. Everything is checked before anything is saved, and imported campaigns
              arrive as drafts so you can review them first.
            </p>
            <Button
              variant="outline"
              className="min-h-11"
              onClick={() =>
                downloadBlob(
                  "coupon-import-template.csv",
                  new Blob([CSV_TEMPLATE], { type: "text/csv;charset=utf-8" }),
                )
              }
            >
              <Download className="w-4 h-4 mr-2" aria-hidden="true" />
              Download template
            </Button>
            <div className="space-y-2">
              <Label htmlFor="import-file">CSV file</Label>
              <Input
                id="import-file"
                type="file"
                accept=".csv,text/csv"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) void handleFile(file);
                }}
              />
            </div>
            {importFatal && <p className="text-sm text-destructive">{importFatal}</p>}
            {importRows.length > 0 && (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {importRows.map((r, i) => {
                  const errs = Object.values(r.errors);
                  return (
                    <div
                      key={`${r.values.code}-${i}`}
                      className="text-sm border border-border rounded-2xl p-3"
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-mono">{r.values.code || "(no code)"}</span>
                        <Badge variant={errs.length ? "destructive" : "secondary"}>
                          {errs.length ? "Needs fixing" : "Ready"}
                        </Badge>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">{r.values.label}</p>
                      {errs.length > 0 && (
                        <p className="text-xs text-destructive mt-1">{errs.join(" · ")}</p>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
            <div className="flex justify-end gap-3">
              <Button variant="outline" className="min-h-12" onClick={() => setImportOpen(false)}>
                Cancel
              </Button>
              <Button
                className="min-h-12"
                disabled={saving || importRows.length === 0}
                onClick={() => void commitImport()}
              >
                Import {importRows.filter((r) => Object.keys(r.errors).length === 0).length} campaigns
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CouponsManager;
